/**
 * Finance Audit registry + contracts (IBA-75).
 *
 * The finance audit trail (`finance_audit_logs`) is a first-class traceability record, separate from the
 * generic {@link ../activity-logs ActivityLog} feed. Its `action` / `sourceType` / `sourceModule`
 * discriminators are CONTROLLED STRINGS (stored as VarChar, not Postgres enums) validated in code against
 * {@link FINANCE_AUDIT_REGISTRY}. Adding a future finance domain (loans, payroll, taxes, depreciation,
 * receivables, payables, asset acquisition/disposal) = add an enum member + a registry entry here, with
 * ZERO database migration. This mirrors the reporting engine's registry-validated `baseEntity` pattern.
 *
 * The enums below exist for autocomplete and to satisfy the repo "prefer enum over string-literal union"
 * rule; the source of truth for what is a VALID audit event is registry membership, not enum exhaustiveness.
 */

import type { MoneyString } from "./money";

/** What happened. Extend by adding a member here + a {@link FINANCE_AUDIT_REGISTRY} entry (no migration). */
export enum FinanceAuditAction {
  TransactionCreated = "TRANSACTION_CREATED",
  TransactionMetadataUpdated = "TRANSACTION_METADATA_UPDATED",
  TransactionReversed = "TRANSACTION_REVERSED",
  TransactionVoided = "TRANSACTION_VOIDED",
  FundCreated = "FUND_CREATED",
  FundUpdated = "FUND_UPDATED",
  FundClosed = "FUND_CLOSED",
  FundReallocated = "FUND_REALLOCATED",
  BudgetApproved = "BUDGET_APPROVED",
  BudgetRevised = "BUDGET_REVISED",
  BudgetLineLinked = "BUDGET_LINE_LINKED",
  OpeningBalanceInitialized = "OPENING_BALANCE_INITIALIZED",
  PeriodClosed = "PERIOD_CLOSED",
  PeriodReopened = "PERIOD_REOPENED",
  /** @deprecated Pre-IBA-161 single-step closure. Retained so historical audit rows stay readable. */
  ReconciliationClosed = "RECONCILIATION_CLOSED",
  /* Reconciliation review (IBA-161). Each transition is audited because a reconciliation is an ASSERTION
     about money — "the books match the bank" — and an assertion without an attributable author and a
     reviewer is not evidence of anything. */
  ReconciliationSubmitted = "RECONCILIATION_SUBMITTED",
  ReconciliationApproved = "RECONCILIATION_APPROVED",
  ReconciliationRejected = "RECONCILIATION_REJECTED",
  ReconciliationWithdrawn = "RECONCILIATION_WITHDRAWN",
  ReceiptIssued = "RECEIPT_ISSUED",
  ReceiptCancelled = "RECEIPT_CANCELLED",
  AccountCreated = "ACCOUNT_CREATED",
  AccountUpdated = "ACCOUNT_UPDATED",
  AccountArchived = "ACCOUNT_ARCHIVED",
  FinanceSetupCompleted = "FINANCE_SETUP_COMPLETED",
  /* IBA-170. Distinct from FinanceSetupCompleted, which records the branch committing its accounting
     identity. This records the branch OPENING ITS BOOKS — the moment operational money became recordable —
     and carries the opening-money/allocation figures it was allowed to open on. "On what basis was this
     branch permitted to start trading?" is a question the identity record cannot answer. */
  FinanceActivated = "FINANCE_ACTIVATED",
  /* ADR-0024. How the organization runs its books, chosen once before the parish finishes setup. It is
     audited because it decides who may see whose money for the life of the workspace, and because it is
     effectively irreversible: undoing it afterwards is a Support/DB action, not a setting. */
  FinanceGovernanceSet = "FINANCE_GOVERNANCE_SET",
  /** The opening money was divided across funds (IBA-170). Rewritable until the books open, then frozen. */
  OpeningFundAllocationSet = "OPENING_FUND_ALLOCATION_SET",
  /* Cross-context transfers (IBA-158). A transfer's lifecycle is audited at every transition, because the
     money is unspendable from SUBMITTED onward even though nothing has posted yet — "who froze this, and
     who decided?" is exactly the question an auditor asks about money that is neither spent nor available. */
  TransferSubmitted = "TRANSFER_SUBMITTED",
  TransferApproved = "TRANSFER_APPROVED",
  TransferRejected = "TRANSFER_REJECTED",
  TransferCancelled = "TRANSFER_CANCELLED",
  /** An approved transfer was corrected by a compensating CASH reversal (no designation is involved). */
  TransferReversed = "TRANSFER_REVERSED",
}

/** The audited business object. Extend for future domains (e.g. `LOAN_REPAYMENT`, `PAYROLL_RUN`). */
export enum FinanceAuditSourceType {
  FinanceTransaction = "FINANCE_TRANSACTION",
  Fund = "FUND",
  Budget = "BUDGET",
  BudgetLine = "BUDGET_LINE",
  FiscalPeriod = "FISCAL_PERIOD",
  OpeningBalance = "OPENING_BALANCE",
  Reconciliation = "RECONCILIATION",
  Receipt = "RECEIPT",
  FinanceAccount = "FINANCE_ACCOUNT",
  FinanceSetup = "FINANCE_SETUP",
  FinanceTransfer = "FINANCE_TRANSFER",
}

/** Which finance domain emitted the event. Extend for future modules (e.g. `LOANS`, `PAYROLL`). */
export enum FinanceAuditSourceModule {
  Finance = "FINANCE",
  Budgeting = "BUDGETING",
}

/** `type` value set for `relatedEntities` / `relatedDocuments` refs. Extend for future domains. */
export enum FinanceAuditRelatedType {
  FinanceTransaction = "FINANCE_TRANSACTION",
  ReversalTransaction = "REVERSAL_TRANSACTION",
  JournalEntry = "JOURNAL_ENTRY",
  Fund = "FUND",
  Activity = "ACTIVITY",
  Budget = "BUDGET",
  BudgetLine = "BUDGET_LINE",
  Pledge = "PLEDGE",
  Member = "MEMBER",
  Group = "GROUP",
  Ministry = "MINISTRY",
  Department = "DEPARTMENT",
  Receipt = "RECEIPT",
  Reconciliation = "RECONCILIATION",
  FiscalPeriod = "FISCAL_PERIOD",
  OpeningBalance = "OPENING_BALANCE",
}

/**
 * The contract a mutation must satisfy to emit an audit event. `requires*` flags are enforced by the
 * writer (`FinanceAuditService.record`) — a registered action that omits a required field throws, which
 * (because audit writes are atomic) rolls back the mutation. This is how "every mutation audited" is a
 * structural invariant rather than a list of remembered call sites.
 */
export interface FinanceAuditActionDefinition {
  action: string;
  sourceType: string;
  sourceModule: string;
  label: string;
  description: string;
  /** The event posts/reverses accounting: it must carry `journalEntryId` + an accountingImpact snapshot. */
  requiresJournalEntry: boolean;
  /** A metadata change: it must carry `oldValues` and `newValues`. */
  requiresOldNew: boolean;
  /** A correction/reversal: it must carry a `reason`. */
  requiresReason: boolean;
}

const def = (
  action: FinanceAuditAction,
  sourceType: FinanceAuditSourceType,
  sourceModule: FinanceAuditSourceModule,
  label: string,
  description: string,
  flags: Partial<Pick<FinanceAuditActionDefinition, "requiresJournalEntry" | "requiresOldNew" | "requiresReason">> = {},
): FinanceAuditActionDefinition => ({
  action,
  sourceType,
  sourceModule,
  label,
  description,
  requiresJournalEntry: flags.requiresJournalEntry ?? false,
  requiresOldNew: flags.requiresOldNew ?? false,
  requiresReason: flags.requiresReason ?? false,
});

/** The single source of truth for valid finance-audit events. */
export const FINANCE_AUDIT_REGISTRY: readonly FinanceAuditActionDefinition[] = [
  def(FinanceAuditAction.TransactionCreated, FinanceAuditSourceType.FinanceTransaction, FinanceAuditSourceModule.Finance,
    "Transaction posted", "An income or expense transaction was recorded and its journal entry posted.",
    { requiresJournalEntry: true }),
  def(FinanceAuditAction.TransactionMetadataUpdated, FinanceAuditSourceType.FinanceTransaction, FinanceAuditSourceModule.Finance,
    "Transaction details updated", "Editable metadata on a posted transaction changed (amounts/journal are immutable).",
    { requiresOldNew: true }),
  def(FinanceAuditAction.TransactionReversed, FinanceAuditSourceType.FinanceTransaction, FinanceAuditSourceModule.Finance,
    "Transaction reversed", "A posted transaction was corrected by a reversal (original marked cancelled). The " +
    "cancellation reason is captured when the operator provides one (the domain allows reasonless reversals)."),
  def(FinanceAuditAction.TransactionVoided, FinanceAuditSourceType.FinanceTransaction, FinanceAuditSourceModule.Finance,
    "Transaction voided", "A transaction was voided."),
  def(FinanceAuditAction.FundCreated, FinanceAuditSourceType.Fund, FinanceAuditSourceModule.Finance,
    "Fund created", "A fund was created."),
  def(FinanceAuditAction.FundUpdated, FinanceAuditSourceType.Fund, FinanceAuditSourceModule.Finance,
    "Fund updated", "A fund's details or spending restrictions were changed.", { requiresOldNew: true }),
  def(FinanceAuditAction.FundClosed, FinanceAuditSourceType.Fund, FinanceAuditSourceModule.Finance,
    "Fund closed", "A fund was closed.", { requiresOldNew: true }),
  def(FinanceAuditAction.FundReallocated, FinanceAuditSourceType.Fund, FinanceAuditSourceModule.Finance,
    "Fund reallocated", "A designation was moved from one fund to another. No physical money moved."),
  def(FinanceAuditAction.BudgetApproved, FinanceAuditSourceType.Budget, FinanceAuditSourceModule.Budgeting,
    "Budget approved", "A budget was approved and made current."),
  def(FinanceAuditAction.BudgetRevised, FinanceAuditSourceType.Budget, FinanceAuditSourceModule.Budgeting,
    "Budget revised", "A budget was revised.", { requiresReason: false }),
  def(FinanceAuditAction.BudgetLineLinked, FinanceAuditSourceType.BudgetLine, FinanceAuditSourceModule.Budgeting,
    "Budget line linked", "A transaction was linked to a budget line for budget-vs-actual."),
  def(FinanceAuditAction.OpeningBalanceInitialized, FinanceAuditSourceType.OpeningBalance, FinanceAuditSourceModule.Finance,
    "Opening balance initialized", "An account's opening balance was set via an opening journal entry.",
    { requiresJournalEntry: true }),
  def(FinanceAuditAction.TransferSubmitted, FinanceAuditSourceType.FinanceTransfer, FinanceAuditSourceModule.Finance,
    "Transfer submitted", "A cross-context transfer was submitted for approval. The money is now FROZEN on the " +
    "source side — held out of available balance — but nothing has posted to the ledger."),
  def(FinanceAuditAction.TransferApproved, FinanceAuditSourceType.FinanceTransfer, FinanceAuditSourceModule.Finance,
    "Transfer approved", "The receiving context accepted a transfer. This is the transition that posts: two " +
    "balanced journal entries, one per branch, and the hold is consumed."),
  def(FinanceAuditAction.TransferRejected, FinanceAuditSourceType.FinanceTransfer, FinanceAuditSourceModule.Finance,
    "Transfer rejected", "The receiving context declined a transfer. The hold is released; nothing ever posted."),
  def(FinanceAuditAction.TransferCancelled, FinanceAuditSourceType.FinanceTransfer, FinanceAuditSourceModule.Finance,
    "Transfer cancelled", "The sending context withdrew a transfer before a decision. The hold is released."),
  def(FinanceAuditAction.TransferReversed, FinanceAuditSourceType.FinanceTransfer, FinanceAuditSourceModule.Finance,
    "Transfer reversed", "An approved transfer was corrected by a compensating reversal. The cash movement is " +
    "undone on both sides; the original stays immutable and linked to its reversal, and no fund designation " +
    "is touched because a transfer never changed any."),
  def(FinanceAuditAction.PeriodClosed, FinanceAuditSourceType.FiscalPeriod, FinanceAuditSourceModule.Finance,
    "Fiscal period closed", "A fiscal period was closed to further posting."),
  def(FinanceAuditAction.PeriodReopened, FinanceAuditSourceType.FiscalPeriod, FinanceAuditSourceModule.Finance,
    "Fiscal period reopened", "A closed fiscal period was reopened."),
  def(FinanceAuditAction.ReconciliationClosed, FinanceAuditSourceType.Reconciliation, FinanceAuditSourceModule.Finance,
    "Reconciliation closed", "An account reconciliation session was closed. Superseded by the submit/approve " +
    "flow (IBA-161); kept in the registry so pre-existing audit rows still resolve to a definition."),
  def(FinanceAuditAction.ReconciliationSubmitted, FinanceAuditSourceType.Reconciliation, FinanceAuditSourceModule.Finance,
    "Reconciliation submitted", "A preparer submitted an account reconciliation for review, asserting that the " +
    "statement and the books agree — or explaining, in a mandatory note, why they do not. The session is now " +
    "locked read-only so the figures cannot move under the reviewer."),
  def(FinanceAuditAction.ReconciliationApproved, FinanceAuditSourceType.Reconciliation, FinanceAuditSourceModule.Finance,
    "Reconciliation approved", "A reviewer accepted a reconciliation. This is the sign-off that makes the period " +
    "proven for that account; the reviewer is never the submitter."),
  def(FinanceAuditAction.ReconciliationRejected, FinanceAuditSourceType.Reconciliation, FinanceAuditSourceModule.Finance,
    "Reconciliation rejected", "A reviewer declined a reconciliation, with a mandatory reason. The session is " +
    "terminal; the preparer redoes the work as a new session.", { requiresReason: true }),
  def(FinanceAuditAction.ReconciliationWithdrawn, FinanceAuditSourceType.Reconciliation, FinanceAuditSourceModule.Finance,
    "Reconciliation withdrawn", "The preparer pulled a submission back to draft before anyone ruled on it, so a " +
    "mistaken submit does not have to be laundered through a rejection the reviewer never intended."),
  def(FinanceAuditAction.ReceiptIssued, FinanceAuditSourceType.Receipt, FinanceAuditSourceModule.Finance,
    "Receipt issued", "A receipt was issued for a posted income/expense transaction."),
  def(FinanceAuditAction.ReceiptCancelled, FinanceAuditSourceType.Receipt, FinanceAuditSourceModule.Finance,
    "Receipt cancelled", "A receipt was cancelled because its transaction was reversed/voided."),
  def(FinanceAuditAction.AccountCreated, FinanceAuditSourceType.FinanceAccount, FinanceAuditSourceModule.Finance,
    "Account created", "A finance account (cash/bank/mobile-money) was added to the chart of accounts."),
  def(FinanceAuditAction.AccountUpdated, FinanceAuditSourceType.FinanceAccount, FinanceAuditSourceModule.Finance,
    "Account updated", "A finance account's editable metadata (name/type/details) changed.",
    { requiresOldNew: true }),
  def(FinanceAuditAction.AccountArchived, FinanceAuditSourceType.FinanceAccount, FinanceAuditSourceModule.Finance,
    "Account archived", "A finance account was archived (or restored); its status changed.",
    { requiresOldNew: true }),
  def(FinanceAuditAction.FinanceSetupCompleted, FinanceAuditSourceType.FinanceSetup, FinanceAuditSourceModule.Finance,
    "Finance setup completed", "A branch's finance identity (base currency, timezone, fiscal-year start, " +
    "conversion date) was locked in when its books were opened."),
  // IBA-170 — the second gate. Neither entry requires a journal entry: the allocation writes fund lines only
  // (the money is already in the ledger from the OPENING entries), and activation writes no accounting at all.
  def(FinanceAuditAction.OpeningFundAllocationSet, FinanceAuditSourceType.OpeningBalance, FinanceAuditSourceModule.Finance,
    "Opening fund allocation set", "The branch's opening money was divided across its funds. Editable until " +
    "the books are opened; a correction afterwards is a fund reallocation."),
  def(FinanceAuditAction.FinanceActivated, FinanceAuditSourceType.FinanceSetup, FinanceAuditSourceModule.Finance,
    "Finance activated", "A branch completed finance setup and opened its books, with its opening money " +
    "fully allocated across funds. Operational transactions become recordable from this moment."),
  def(FinanceAuditAction.FinanceGovernanceSet, FinanceAuditSourceType.FinanceSetup, FinanceAuditSourceModule.Finance,
    "Finance governance set", "The workspace chose whether the parish reads every branch's money in full " +
    "or only their health ratios (ADR-0024). Settled once the parish completes its finance setup."),
];

const REGISTRY_BY_ACTION: ReadonlyMap<string, FinanceAuditActionDefinition> = new Map(
  FINANCE_AUDIT_REGISTRY.map((entry) => [entry.action, entry]),
);

export function getFinanceAuditActionDefinition(action: string): FinanceAuditActionDefinition | null {
  return REGISTRY_BY_ACTION.get(action) ?? null;
}

export function isRegisteredFinanceAuditAction(value: unknown): value is FinanceAuditAction {
  return typeof value === "string" && REGISTRY_BY_ACTION.has(value);
}

export function listFinanceAuditActions(): readonly FinanceAuditActionDefinition[] {
  return FINANCE_AUDIT_REGISTRY;
}

/** A polymorphic reference stored in `relatedEntities` / `relatedDocuments` (bounded JSON list). */
export interface FinanceAuditRef {
  type: string;
  id: string;
  label?: string;
}

/** One frozen debit/credit line inside {@link FinanceAuditAccountingImpact}. */
export interface FinanceAuditImpactLine {
  direction: "DEBIT" | "CREDIT";
  accountingAccountId: string;
  accountCode: string;
  /** Frozen at event time — the account can be renamed later without changing the audit record. */
  accountLabel: string;
  /**
   * The posted amount, as a canonical decimal string at the entry's currency scale (IBA-116).
   *
   * A journal line is the most precision-critical money in the system — it is what the trial balance is
   * summed from. Freezing it as a float meant the audit trail, the one record whose whole purpose is to be
   * trustworthy later, was the record that could not be trusted to the cent.
   */
  amount: MoneyString;
  financeAccountId?: string | null;
  fundId?: string | null;
}

/**
 * Bounded, frozen snapshot of a posted event's accounting effect. Stable references (`journalEntryId`) plus
 * the essential effect — NOT a permanent duplicate of the journal (which is itself immutable). The detail
 * API re-reads the live journal for the authoritative view; this snapshot preserves frozen account labels
 * and lets a single audit row stand alone.
 */
export interface FinanceAuditAccountingImpact {
  journalEntryId: string;
  entryStatus: string;
  currency: string;
  entryDate: string;
  postedAt: string;
  /** Σ of the debit lines, at `currency`'s scale. Exact — summed in decimal, never in float. */
  totalDebit: MoneyString;
  /** Σ of the credit lines, at `currency`'s scale. Equal to {@link totalDebit} for a balanced entry. */
  totalCredit: MoneyString;
  fiscalPeriodLabel?: string | null;
  fiscalPeriodStatusAtEvent?: string | null;
  lines: FinanceAuditImpactLine[];
}
